import React, { useState, useEffect } from 'react';
import { db1 } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  doc, 
  serverTimestamp,
  increment 
} from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Image, Button, Spinner, Alert, Badge, Row, Col, Modal } from 'react-bootstrap';

const VideoPostPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isArabic, setIsArabic] = useState(true);
  const [hasLiked, setHasLiked] = useState(false);
  const [showCopiedAlert, setShowCopiedAlert] = useState(false);

  // Red Color Scheme for Media
  const PRIMARY_RED = '#dc3545';
  const DARK_RED = '#a71e2a';
  const LIGHT_RED = '#e74c3c';
  const VERY_LIGHT_RED = '#fff5f5';
  const RED_TEXT = '#2c0b0e';
  const RED_BORDER = '#e74c3c';

  const translations = {
    'Back to Videos': 'العودة إلى الفيديوهات',
    'Published on': 'نشر في',
    'Views': 'مشاهدات',
    'Likes': 'إعجابات',
    'Tags': 'الكلمات الدلالية',
    'Author': 'المؤلف',
    'Category': 'الفئة',
    'Error loading video': 'خطأ في تحميل الفيديو',
    'Video not found': 'الفيديو غير موجود',
    'Like': 'أعجبني',
    'Share': 'مشاركة',
    'Loading video...': 'جاري تحميل الفيديو...',
    'You already liked this video': 'لقد أعجبت بهذا الفيديو مسبقاً',
    'Thank you for your like!': 'شكراً لإعجابك!',
    'Liked': 'معجب به',
    'Link copied to clipboard!': 'تم نسخ الرابط إلى الحافظة!',
    'Duration': 'المدة',
    'Watch Video': 'مشاهدة الفيديو',
    'Field Report': 'تقرير ميداني',
    'Documentary': 'فيلم وثائقي',
    'Interview': 'مقابلة',
    'Training': 'تدريب',
    'Statement': 'بيان'
  };

  const translateText = (text) => {
    if (text === video?.description) {
      return text;
    }
    return isArabic ? (translations[text] || text) : text;
  };

  const fontStyles = {
    arabic: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      lineHeight: '1.8'
    },
    english: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      lineHeight: '1.6'
    }
  };

  const currentFontStyle = isArabic ? fontStyles.arabic : fontStyles.english;

  const checkIfLiked = (videoToken) => {
    const likedVideos = JSON.parse(localStorage.getItem('likedVideos') || '{}');
    return likedVideos[videoToken] === true;
  };

  const incrementViewCount = async (videoId) => {
    try {
      const videoRef = doc(db1, 'videos', videoId);
      await updateDoc(videoRef, {
        views: increment(1),
        lastViewed: serverTimestamp()
      });
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      if (document.activeElement) {
        document.activeElement.blur();
      }
      
      await navigator.clipboard.writeText(text);
      setShowCopiedAlert(true);
      
      setTimeout(() => {
        setShowCopiedAlert(false);
      }, 2000);
      
    } catch (err) {
      console.error('Failed to copy: ', err);
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setShowCopiedAlert(true);
        setTimeout(() => setShowCopiedAlert(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
        alert('Failed to copy link');
      }
      document.body.removeChild(textArea);
    }
  };

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!token) {
          setError('No token provided');
          setLoading(false);
          return;
        }

        const videoQuery = query(
          collection(db1, 'videos'),
          where('token', '==', token)
        );

        const querySnapshot = await getDocs(videoQuery);
        
        if (!querySnapshot.empty) {
          const videoDoc = querySnapshot.docs[0];
          const videoData = videoDoc.data();

          const videoWithData = {
            id: videoDoc.id,
            ...videoData,
            createdAt: videoData.createdAt?.toDate() || new Date(),
            tags: Array.isArray(videoData.tags) ? videoData.tags : [],
            views: videoData.views || 0,
            likes: videoData.likes || 0
          };

          setVideo(videoWithData);
          setHasLiked(checkIfLiked(token));

          await incrementViewCount(videoDoc.id);

        } else {
          setError('Video not found');
        }

      } catch (err) {
        console.error('Error fetching video:', err);
        setError(`Error loading video: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchVideo();
  }, [token]);

  const handleLike = async () => {
    if (!video) return;

    if (hasLiked) {
      alert(translateText('You already liked this video'));
      return;
    }

    try {
      await updateDoc(doc(db1, 'videos', video.id), {
        likes: increment(1),
        lastLiked: serverTimestamp()
      });

      setVideo(prev => ({
        ...prev,
        likes: (prev.likes || 0) + 1
      }));
      setHasLiked(true);

      const likedVideos = JSON.parse(localStorage.getItem('likedVideos') || '{}');
      likedVideos[video.token] = true;
      localStorage.setItem('likedVideos', JSON.stringify(likedVideos));

      alert(translateText('Thank you for your like!'));

    } catch (err) {
      console.error('Error updating likes:', err);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryBadge = (category) => {
    const categories = {
      'field_report': { text: translateText('Field Report'), color: PRIMARY_RED },
      'documentary': { text: translateText('Documentary'), color: '#28a745' },
      'interview': { text: translateText('Interview'), color: '#007bff' },
      'training': { text: translateText('Training'), color: '#6f42c1' },
      'statement': { text: translateText('Statement'), color: DARK_RED }
    };
    return categories[category] || { text: category, color: PRIMARY_RED };
  };

  const isYouTubeUrl = (url) => {
    return url?.includes('youtube.com') || url?.includes('youtu.be');
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    
    return url;
  };

  if (loading) {
    return (
      <Container className="my-5 text-center" style={currentFontStyle}>
        <Spinner 
          animation="border" 
          style={{ 
            color: PRIMARY_RED,
            width: '3rem', 
            height: '3rem' 
          }} 
        />
        <p className="mt-3" style={{ color: PRIMARY_RED, fontSize: '1.1rem' }}>
          {translateText('Loading video...')}
        </p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5" style={currentFontStyle}>
        <Alert variant="danger">
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
        </Alert>
        <Button 
          variant="primary" 
          onClick={() => navigate('/videos')}
          style={{ 
            backgroundColor: PRIMARY_RED, 
            borderColor: PRIMARY_RED,
            borderRadius: '8px'
          }}
        >
          {translateText('Back to Videos')}
        </Button>
      </Container>
    );
  }

  if (!video) {
    return (
      <Container className="my-5" style={currentFontStyle}>
        <Alert variant="warning">
          {translateText('Video not found')}
        </Alert>
        <Button 
          variant="primary" 
          onClick={() => navigate('/videos')}
          style={{ 
            backgroundColor: PRIMARY_RED, 
            borderColor: PRIMARY_RED,
            borderRadius: '8px'
          }}
        >
          {translateText('Back to Videos')}
        </Button>
      </Container>
    );
  }

  const categoryBadge = getCategoryBadge(video.category);
  const embedUrl = getYouTubeEmbedUrl(video.videoUrl);

  return (
    <Container className="my-5" style={{ 
      maxWidth: '900px',
      direction: isArabic ? 'rtl' : 'ltr',
      ...currentFontStyle
    }}>
      {/* Copied Alert */}
      {showCopiedAlert && (
        <Alert 
          variant="success" 
          className="position-fixed top-0 start-50 translate-middle-x mt-3"
          style={{ zIndex: 9999, minWidth: '300px' }}
        >
          <div className="d-flex align-items-center">
            <i className="bi bi-check-circle-fill me-2"></i>
            {translateText('Link copied to clipboard!')}
          </div>
        </Alert>
      )}

      {/* Navigation */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button 
          variant="outline-primary"
          onClick={() => navigate('/videos')}
          style={{
            borderColor: PRIMARY_RED,
            color: PRIMARY_RED,
            borderRadius: '20px',
            fontWeight: '600'
          }}
        >
          <i className="bi bi-arrow-left me-2"></i>
          {translateText('Back to Videos')}
        </Button>
        <Button 
          onClick={() => setIsArabic(!isArabic)}
          variant="outline-primary"
          size="sm"
          style={{
            borderColor: PRIMARY_RED,
            color: PRIMARY_RED,
            borderRadius: '20px',
            fontWeight: '600'
          }}
        >
          {isArabic ? 'English' : 'العربية'}
        </Button>
      </div>

      <Card className="shadow-lg" style={{ 
        border: 'none',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: VERY_LIGHT_RED
      }}>
        {/* Video Player */}
        <div className="position-relative">
          {embedUrl && isYouTubeUrl(video.videoUrl) ? (
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={embedUrl}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          ) : (
            <div className="text-center p-5" style={{ backgroundColor: '#f8f9fa' }}>
              <div style={{ fontSize: '4rem', color: PRIMARY_RED, marginBottom: '1rem' }}>
                🎥
              </div>
              <h4 style={{ color: DARK_RED }}>{translateText('Watch Video')}</h4>
              <p style={{ color: PRIMARY_RED }}>
                <a href={video.videoUrl} target="_blank" rel="noopener noreferrer">
                  {translateText('Click here to watch the video')}
                </a>
              </p>
            </div>
          )}
          
          {/* Duration Badge */}
          {video.duration && (
            <Badge 
              className="position-absolute bottom-0 end-0 m-3"
              style={{ 
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: 'white',
                fontSize: '0.9rem',
                padding: '6px 12px',
                fontWeight: '600'
              }}
            >
              <i className="bi bi-clock me-1"></i>
              {video.duration}
            </Badge>
          )}
        </div>

        <Card.Body style={{ padding: '40px' }}>
          {/* Meta Information */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
            <Badge 
              style={{ 
                backgroundColor: categoryBadge.color, 
                color: 'white',
                fontSize: '0.9rem',
                padding: '8px 16px',
                fontWeight: '600'
              }}
            >
              {categoryBadge.text}
            </Badge>
            <div style={{ color: DARK_RED, fontSize: '0.9rem', fontWeight: '600' }}>
              <i className="bi bi-clock me-1"></i>
              {formatDate(video.createdAt)}
            </div>
          </div>

          {/* Title */}
          <h1 style={{ 
            color: DARK_RED, 
            fontWeight: '700',
            fontSize: '2.5rem',
            lineHeight: '1.2',
            marginBottom: '1rem'
          }}>
            {video.title || 'Untitled Video'}
          </h1>

          {/* Author and Stats */}
          <Row className="align-items-center mb-4">
            <Col md={6}>
              <div className="d-flex align-items-center">
                <div style={{ 
                  width: '50px', 
                  height: '50px', 
                  borderRadius: '50%',
                  backgroundColor: PRIMARY_RED,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  marginRight: isArabic ? '0' : '15px',
                  marginLeft: isArabic ? '15px' : '0'
                }}>
                  {video.author?.charAt(0) || 'A'}
                </div>
                <div>
                  <div style={{ fontWeight: '700', color: DARK_RED, fontSize: '1.1rem' }}>
                    {video.author || 'Al-Majd Media'}
                  </div>
                  <small style={{ color: PRIMARY_RED, opacity: '0.8', fontWeight: '500' }}>
                    {translateText('Author')}
                  </small>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex justify-content-end gap-4" style={{ color: DARK_RED }}>
                <div className="text-center">
                  <div style={{ fontWeight: '700', fontSize: '1.2rem' }}>
                    {video.views || 0}
                  </div>
                  <small style={{ opacity: '0.8', fontWeight: '500' }}>{translateText('Views')}</small>
                </div>
                <div className="text-center">
                  <div style={{ fontWeight: '700', fontSize: '1.2rem' }}>
                    {video.likes || 0}
                  </div>
                  <small style={{ opacity: '0.8', fontWeight: '500' }}>{translateText('Likes')}</small>
                </div>
              </div>
            </Col>
          </Row>

          {/* Description */}
          <div style={{ 
            color: RED_TEXT,
            lineHeight: currentFontStyle.lineHeight,
            fontSize: '1.1rem',
            marginBottom: '3rem',
            whiteSpace: 'pre-line',
            fontWeight: '500'
          }}>
            {video.description || (
              <p style={{ color: PRIMARY_RED, fontStyle: 'italic', opacity: '0.7' }}>
                No description available for this video.
              </p>
            )}
          </div>

          {/* Tags */}
          {video.tags && video.tags.length > 0 && (
            <div className="mb-4">
              <h6 style={{ color: DARK_RED, marginBottom: '0.5rem', fontWeight: '700' }}>
                {translateText('Tags')}:
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {video.tags.map((tag, index) => (
                  <Badge 
                    key={index}
                    style={{ 
                      backgroundColor: 'white',
                      color: PRIMARY_RED,
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: `2px solid ${RED_BORDER}`,
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    }}
                  >
                    #{tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="d-flex gap-3 pt-4 border-top" style={{ borderColor: RED_BORDER + '!important' }}>
            <Button 
              variant={hasLiked ? "success" : "outline-primary"}
              onClick={handleLike}
              disabled={hasLiked}
              className="d-flex align-items-center"
              style={{ 
                borderRadius: '8px',
                fontWeight: '600',
                ...(hasLiked ? {
                  backgroundColor: LIGHT_RED,
                  borderColor: LIGHT_RED
                } : {
                  color: PRIMARY_RED,
                  borderColor: PRIMARY_RED,
                  backgroundColor: 'transparent'
                })
              }}
            >
              <i className={`bi ${hasLiked ? 'bi-hand-thumbs-up-fill' : 'bi-hand-thumbs-up'} me-2`}></i>
              {hasLiked ? translateText('Liked') : translateText('Like')} ({video.likes || 0})
            </Button>
            <Button 
              variant="outline-primary"
              onClick={() => copyToClipboard(window.location.href)}
              className="d-flex align-items-center"
              style={{ 
                borderRadius: '8px',
                color: PRIMARY_RED,
                borderColor: PRIMARY_RED,
                backgroundColor: 'transparent',
                fontWeight: '600'
              }}
            >
              <i className="bi bi-share me-2"></i>
              {translateText('Share')}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default VideoPostPage;
